import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Select, CardContainer } from '@tviklax/ui-components';
import { useRole } from '../../../context/RoleContext';
import { logAudit } from '../../../shared/audit/audit';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[6]}`};
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]}`};
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  gap: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing[3]};
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray600};
  margin: 0;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0 0 ${({ theme }) => theme.spacing[3]} 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.gray700};
`;

const Input = styled.input`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray900};

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.gray100};
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray900};
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.gray100};
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.button<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  background: #ffffff;
  transition: all 0.15s ease;
  
  @media (max-width: 640px) {
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    gap: ${({ theme }) => theme.spacing[2]};
  }
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  ${({ $status, theme }) => {
    switch ($status) {
      case 'not-started':
        return `
          border: 2px solid ${theme.colors.gray300};
          color: ${theme.colors.gray700};
        `;
      case 'in-production':
        return `
          border: 2px solid ${theme.colors.primary};
          color: ${theme.colors.primary};
        `;
      case 'qc-finishing':
        return `
          border: 2px solid ${theme.colors.warning};
          color: ${theme.colors.warningDark};
        `;
      case 'ready-for-dispatch':
        return `
          border: 2px solid ${theme.colors.success};
          color: ${theme.colors.success};
        `;
      case 'delayed':
        return `
          border: 2px solid ${theme.colors.error};
          color: ${theme.colors.error};
        `;
      default:
        return `
          border: 2px solid ${theme.colors.gray300};
          color: ${theme.colors.gray700};
        `;
    }
  }}
`;

const StatusDot = styled.div<{ $status: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  
  ${({ $status, theme }) => {
    switch ($status) {
      case 'not-started':
        return `background: ${theme.colors.gray400};`;
      case 'in-production':
        return `background: ${theme.colors.primary};`;
      case 'qc-finishing':
        return `background: ${theme.colors.warning};`;
      case 'ready-for-dispatch':
        return `background: ${theme.colors.success};`;
      case 'delayed':
        return `background: ${theme.colors.error};`;
      default:
        return `background: ${theme.colors.gray400};`;
    }
  }}
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[3]};
  }
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const MetaLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray600};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MetaValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const AlertBox = styled.div<{ $type: 'info' | 'warning' | 'success' }>`
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  ${({ $type, theme }) => {
    switch ($type) {
      case 'warning':
        return `
          background: ${theme.colors.warningLight};
          color: ${theme.colors.warningDark};
          border-left: 4px solid ${theme.colors.warning};
        `;
      case 'success':
        return `
          background: ${theme.colors.successLight};
          color: ${theme.colors.successDark};
          border-left: 4px solid ${theme.colors.success};
        `;
      case 'info':
      default:
        return `
          background: ${theme.colors.infoLight};
          color: ${theme.colors.infoDark};
          border-left: 4px solid ${theme.colors.primary};
        `;
    }
  }}
`;

const HistoryItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  border-left: 2px solid ${({ theme }) => theme.colors.gray200};
  
  &:last-child {
    border-left-color: transparent;
  }
`;

const HistoryDot = styled.div<{ $status: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
  
  ${({ $status, theme }) => {
    switch ($status) {
      case 'delayed':
        return `background: ${theme.colors.error};`;
      case 'ready-for-dispatch':
        return `background: ${theme.colors.success};`;
      case 'in-production':
        return `background: ${theme.colors.primary};`;
      default:
        return `background: ${theme.colors.gray400};`;
    }
  }}
`;

const HistoryContent = styled.div`
  flex: 1;
`;

const HistoryTimestamp = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray600};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const HistoryText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray900};
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 640px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

type ProductionStatusType = 'not-started' | 'in-production' | 'qc-finishing' | 'delayed' | 'ready-for-dispatch';
type AttachmentType = 'qc-report' | 'production-photo' | 'certificate' | 'other';

interface Attachment {
  name: string;
  url: string;
  uploadedAt: string;
  type: AttachmentType;
}

interface ProductionData {
  orderId: string;
  customerName: string;
  productionStatus: ProductionStatusType;
  expectedCompletionDate: string;
  lastUpdated: string;
  updatedBy: string;
  notes: string;
  supplier: string;
  attachments?: Attachment[];
}

interface HistoryEntry {
  timestamp: string;
  status: ProductionStatusType;
  updatedBy: string;
  notes?: string;
  attachments?: Attachment[];
}

interface ProductionPageProps {
  selectedOrderId?: string;
  onOrderChange?: (orderId: string) => void;
  onBack?: () => void;
  orders?: Array<{
    id: string;
    customer: string;
    supplier: string;
    status: ProductionStatusType;
    expectedCompletionDate: string;
    notes?: string;
    lastUpdated?: string;
    updatedBy?: string;
    history?: Array<{
      statusChange: ProductionStatusType;
      timestamp: string;
      changedBy: string;
      notes?: string | null;
    }>;
    attachments?: Array<{
      fileName: string;
      fileUrl: string;
      fileType?: string | null;
      uploadedDate?: string | null;
    }>;
  }>;
  onSaveProductionUpdate?: (payload: {
    status: ProductionStatusType;
    expectedCompletionDate: string;
    notes: string;
    actor: string;
  }) => Promise<void>;
}

export const ProductionStatus: React.FC<ProductionPageProps> = ({
  selectedOrderId = '',
  onBack,
  onSaveProductionUpdate,
  orders = [],
}) => {
  const { role } = useRole();
  const isSupplier = role === 'supplier';

  const currentOrder = orders.find(o => o.id === selectedOrderId) || orders[0];

  const [productionData, setProductionData] = useState<ProductionData>({
    orderId: currentOrder?.id || '',
    customerName: currentOrder?.customer || 'Unknown Customer',
    productionStatus: currentOrder?.status || 'not-started',
    expectedCompletionDate: currentOrder?.expectedCompletionDate || '',
    lastUpdated: currentOrder?.lastUpdated || new Date().toISOString(),
    updatedBy: currentOrder?.updatedBy || `Supplier: ${currentOrder?.supplier || 'Unknown Supplier'}`,
    notes: currentOrder?.notes || '',
    supplier: currentOrder?.supplier || 'Unknown Supplier',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(productionData);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileTypes, setFileTypes] = useState<AttachmentType[]>([]);
  const [validationError, setValidationError] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  // Update production data when selected order changes
  useEffect(() => {
    const newOrder = orders.find(o => o.id === selectedOrderId) || orders[0];
    if (!newOrder) {
      setHistory([]);
      setProductionData({
        orderId: '',
        customerName: 'Unknown Customer',
        supplier: 'Unknown Supplier',
        productionStatus: 'not-started',
        expectedCompletionDate: '',
        updatedBy: 'System',
        lastUpdated: new Date().toISOString(),
        notes: '',
        attachments: [],
      });
      return;
    }

    const historyFromApi: HistoryEntry[] = (newOrder.history || []).map((entry) => ({
      timestamp: entry.timestamp,
      status: entry.statusChange,
      updatedBy: entry.changedBy,
      notes: entry.notes || undefined,
    }));
    const attachmentsFromApi: Attachment[] = (newOrder.attachments || []).map((attachment) => ({
      name: attachment.fileName,
      url: attachment.fileUrl,
      uploadedAt: attachment.uploadedDate || new Date().toISOString(),
      type: (attachment.fileType as AttachmentType) || 'other',
    }));
    const updatedData = {
      orderId: newOrder.id,
      customerName: newOrder.customer,
      supplier: newOrder.supplier,
      productionStatus: newOrder.status,
      expectedCompletionDate: newOrder.expectedCompletionDate,
      updatedBy: newOrder.updatedBy || `Supplier: ${newOrder.supplier}`,
      lastUpdated: newOrder.lastUpdated || new Date().toISOString(),
      notes: newOrder.notes || '',
      attachments: attachmentsFromApi,
    };
    setProductionData(updatedData);
    setFormData(updatedData);
    setHistory(historyFromApi);
  }, [selectedOrderId, orders]);

  const statusOptions = [
    { label: 'Not Started', value: 'not-started' },
    { label: 'In Production', value: 'in-production' },
    { label: 'QC / Finishing', value: 'qc-finishing' },
    { label: 'Delayed', value: 'delayed' },
    { label: 'Ready for Dispatch', value: 'ready-for-dispatch' },
  ];

  const attachmentTypeOptions = [
    { label: 'QC Report', value: 'qc-report' },
    { label: 'Production Photo', value: 'production-photo' },
    { label: 'Certificate', value: 'certificate' },
    { label: 'Other', value: 'other' },
  ];

  // Auto-suggest attachment type based on status
  const getDefaultAttachmentType = (status: ProductionStatusType): AttachmentType => {
    switch (status) {
      case 'qc-finishing':
        return 'qc-report';
      case 'ready-for-dispatch':
        return 'certificate';
      default:
        return 'production-photo';
    }
  };

  // State machine: determine valid next states based on current status
  const getValidNextStates = (currentStatus: ProductionStatusType): ProductionStatusType[] => {
    switch (currentStatus) {
      case 'not-started':
        return ['in-production', 'delayed'];
      case 'in-production':
        return ['qc-finishing', 'delayed'];
      case 'qc-finishing':
        return ['ready-for-dispatch', 'delayed'];
      case 'delayed':
        return ['in-production', 'qc-finishing', 'ready-for-dispatch'];
      case 'ready-for-dispatch':
        return []; // Terminal state - cannot change
      default:
        return [];
    }
  };

  const validNextStates = getValidNextStates(productionData.productionStatus);
  const availableStatusOptions = statusOptions.filter(
    opt => opt.value === productionData.productionStatus || validNextStates.includes(opt.value as ProductionStatusType)
  );

  const handleSave = async () => {
    // Show confirmation modal for Ready for Dispatch
    const isReadyForDispatch = formData.productionStatus === 'ready-for-dispatch';
    const wasNotReady = productionData.productionStatus !== 'ready-for-dispatch';
    
    if (isReadyForDispatch && wasNotReady && !showConfirmModal) {
      setShowConfirmModal(true);
      return;
    }

    // Validation: allow historical dates already on record, but block changing date to a new past value.
    const currentExpectedDate = productionData.expectedCompletionDate || '';
    const nextExpectedDate = formData.expectedCompletionDate || '';
    const hasDateChanged = nextExpectedDate !== currentExpectedDate;
    if (hasDateChanged && nextExpectedDate) {
      const completionDate = new Date(nextExpectedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (!Number.isNaN(completionDate.getTime()) && completionDate < today) {
        setValidationError('Expected completion date cannot be in the past');
        return;
      }
    }

    // If changing completion date after "In Production", this would trigger notification (backend)
    const dateChanged = formData.expectedCompletionDate !== productionData.expectedCompletionDate;
    const inProductionOrLater = ['in-production', 'qc-finishing', 'delayed'].includes(productionData.productionStatus);
    
    if (dateChanged && inProductionOrLater) {
      console.log('[TRIGGER] Expected completion date changed - notify Sales + Ops');
    }

    // Special handling for "Ready for Dispatch"
    if (isReadyForDispatch && wasNotReady) {
      console.log('[TRIGGER] PRODUCTION_COMPLETED event');
      console.log('[NOTIFICATION] → Ops: Scheduling unlocked');
      console.log('[NOTIFICATION] → Sales: Production complete');
      console.log('[AUDIT] Production status locked - cannot be changed after Ready for Dispatch');
    }

    logAudit(
      'production-status-updated',
      'supplier',
      { status: productionData.productionStatus, expectedCompletion: productionData.expectedCompletionDate },
      { status: formData.productionStatus, expectedCompletion: formData.expectedCompletionDate },
      productionData.supplier
    );

    try {
      setIsSaving(true);
      if (onSaveProductionUpdate) {
        await onSaveProductionUpdate({
          status: formData.productionStatus,
          expectedCompletionDate: formData.expectedCompletionDate || '',
          notes: formData.notes,
          actor: `Supplier: ${productionData.supplier}`,
        });
        console.log('[ProductionStatus] Save completed successfully');
      }
      
      setIsEditing(false);
      setSelectedFiles([]);
      setFileTypes([]);
      setValidationError('');
      setShowConfirmModal(false);
      setIsSaving(false);
    } catch (error) {
      console.error('[ProductionStatus Save Error]', error);
      setValidationError(error instanceof Error ? error.message : 'Unable to save production update');
      setIsSaving(false);
      return;
    }
  };

  const handleCancel = () => {
    setFormData(productionData);
    setIsEditing(false);
    setSelectedFiles([]);
    setFileTypes([]);
    setValidationError('');
    setShowConfirmModal(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const invalidFiles = files.filter(file => !validTypes.includes(file.type) || file.size > maxSize);
    
    if (invalidFiles.length > 0) {
      setValidationError('Only PDF, JPG, PNG files under 10MB are allowed');
      return;
    }

    setSelectedFiles(prev => [...prev, ...files]);
    setFileTypes(prev => [...prev, ...files.map(() => getDefaultAttachmentType(formData.productionStatus))]);
    setValidationError('');
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFileTypes(prev => prev.filter((_, i) => i !== index));
  };

  const getStatusLabel = (status: ProductionStatusType) => {
    return statusOptions.find(opt => opt.value === status)?.label || status;
  };

  if (!currentOrder) {
    return (
      <Page>
        <PageHeader>
          <HeaderContent>
            <Title>Production Status</Title>
            <Subtitle>Track manufacturing progress</Subtitle>
          </HeaderContent>
          {onBack && (
            <Button onClick={onBack} variant="outline">
              ← Back to Orders
            </Button>
          )}
        </PageHeader>
        <CardContainer variant="elevated" padding="md">
          <SectionTitle>No Production Orders</SectionTitle>
          <AlertBox $type="info">
            No production records were returned from the database.
          </AlertBox>
        </CardContainer>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <HeaderContent>
          <Title>Production Status</Title>
          <Subtitle>Track manufacturing progress</Subtitle>
        </HeaderContent>
        <div style={{ display: 'flex', gap: 12 }}>
          {onBack && (
            <Button onClick={onBack} variant="outline">
              ← Back to Orders
            </Button>
          )}
          {!isEditing && productionData.productionStatus !== 'ready-for-dispatch' && (
            <Button 
              onClick={() => setIsEditing(true)} 
              variant="default"
              disabled={!isSupplier}
              title={!isSupplier ? 'Only supplier can update production status' : 'Update production status'}
            >
              Update Status
            </Button>
          )}
        </div>
        {productionData.productionStatus === 'ready-for-dispatch' && (
          <div style={{ color: '#059669', fontSize: 14, fontWeight: 600 }}>✅ Production Complete (Locked)</div>
        )}
      </PageHeader>

      {/* Order Info Bar */}
      <div style={{ display: 'flex', gap: 16, padding: 12, background: '#f3f4f6', borderRadius: 8, border: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Order</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{productionData.orderId}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{productionData.customerName}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Supplier</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{productionData.supplier}</div>
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Current Status Card */}
          <CardContainer variant="elevated" padding="md">
            <SectionTitle>Current Status</SectionTitle>
            
            <MetaGrid>
              <MetaItem>
                <MetaLabel>Order ID</MetaLabel>
                <MetaValue>{productionData.orderId}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Customer</MetaLabel>
                <MetaValue>{productionData.customerName}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Supplier</MetaLabel>
                <MetaValue>{productionData.supplier}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Production Status</MetaLabel>
                <div>
                  <StatusBadge 
                    $status={productionData.productionStatus} 
                    onClick={() => {
                      if (isSupplier && productionData.productionStatus !== 'ready-for-dispatch') {
                        setIsEditing(true);
                      }
                    }}
                    title={`Last updated ${new Date(productionData.lastUpdated).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                  >
                    <StatusDot $status={productionData.productionStatus} />
                    {getStatusLabel(productionData.productionStatus)}
                    {productionData.productionStatus === 'ready-for-dispatch' && (
                      <span style={{ marginLeft: 4 }}>🔒</span>
                    )}
                  </StatusBadge>
                </div>
              </MetaItem>
            </MetaGrid>

            {productionData.productionStatus === 'delayed' && (
              <AlertBox $type="warning" style={{ marginTop: 16 }}>
                ⚠️ Production is delayed. Scheduling has been flagged as "At risk". Sales and Ops teams have been notified.
              </AlertBox>
            )}

            {productionData.productionStatus === 'ready-for-dispatch' && (
              <AlertBox $type="success" style={{ marginTop: 16 }}>
                ✅ Production complete! Scheduling can now proceed to "Ready" status.
              </AlertBox>
            )}
          </CardContainer>

          {/* Update Form (Supplier Only) */}
          {isEditing && (
            <CardContainer variant="elevated" padding="md">
              <SectionTitle>Update Production Status</SectionTitle>
              
              {validationError && (
                <AlertBox $type="warning" style={{ marginBottom: 16 }}>
                  {validationError}
                </AlertBox>
              )}
              
              <FormGroup>
                <Label>Production Status *</Label>
                <Select
                  value={formData.productionStatus}
                  onChange={(e) => setFormData({ ...formData, productionStatus: e.target.value as ProductionStatusType })}
                  items={availableStatusOptions}
                />
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {productionData.productionStatus === 'ready-for-dispatch' 
                    ? 'Status is locked at Ready for Dispatch'
                    : 'Only valid next states are shown'}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Expected Completion Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.expectedCompletionDate}
                  onChange={(e) => setFormData({ ...formData, expectedCompletionDate: e.target.value })}
                  disabled={productionData.productionStatus === 'ready-for-dispatch'}
                />
                {productionData.productionStatus === 'ready-for-dispatch' && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    🔒 Date is locked after Ready for Dispatch
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any relevant notes about production progress..."
                />
              </FormGroup>

              <FormGroup>
                <Label>Attachments (Optional)</Label>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                  Upload production evidence (QC report, photos). PDF, JPG, PNG only. Max 10MB.
                </div>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={handleFileChange}
                />
                {selectedFiles.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {selectedFiles.map((file, index) => (
                      <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: 8, background: '#f9fafb', borderRadius: 6 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</div>
                          <Select
                            value={fileTypes[index]}
                            onChange={(e) => {
                              const newTypes = [...fileTypes];
                              newTypes[index] = e.target.value as AttachmentType;
                              setFileTypes(newTypes);
                            }}
                            items={attachmentTypeOptions}
                          />
                        </div>
                        <button onClick={() => removeFile(index)} style={{ color: '#ef4444', cursor: 'pointer', border: 'none', background: 'none', fontSize: 12, marginTop: 4 }}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </FormGroup>

              <FormActions>
                <Button onClick={handleSave} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Changes'}</Button>
                <Button variant="outline" onClick={handleCancel} disabled={isSaving}>Cancel</Button>
              </FormActions>
            </CardContainer>
          )}

          {/* Details Card (Read-only for non-suppliers) */}
          {!isEditing && (
            <CardContainer variant="elevated" padding="md">
              <SectionTitle>Production Details</SectionTitle>
              
              <MetaGrid>
                <MetaItem>
                  <MetaLabel>Expected Completion</MetaLabel>
                  <MetaValue>{new Date(productionData.expectedCompletionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Last Updated</MetaLabel>
                  <MetaValue>{new Date(productionData.lastUpdated).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Updated By</MetaLabel>
                  <MetaValue>{productionData.updatedBy}</MetaValue>
                </MetaItem>
              </MetaGrid>

              {productionData.notes && (
                <div style={{ marginTop: 16 }}>
                  <MetaLabel style={{ marginBottom: 8 }}>Notes</MetaLabel>
                  <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
                    {productionData.notes}
                  </div>
                </div>
              )}

              {productionData.attachments && productionData.attachments.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <MetaLabel style={{ marginBottom: 8 }}>Attachments</MetaLabel>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {productionData.attachments.map((attachment, index) => (
                      <a 
                        key={index} 
                        href={attachment.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          fontSize: 13, 
                          color: '#3b82f6', 
                          textDecoration: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8
                        }}
                      >
                        📎 {attachment.name}
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>
                          ({attachmentTypeOptions.find(opt => opt.value === attachment.type)?.label || 'Other'})
                        </span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>• {new Date(attachment.uploadedAt).toLocaleDateString()}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </CardContainer>
          )}

          {/* History Card */}
          <CardContainer variant="elevated" padding="md">
            <SectionTitle>Status History</SectionTitle>
            
            <div>
              {history.map((entry, index) => (
                <HistoryItem key={index}>
                  <HistoryDot $status={entry.status} />
                  <HistoryContent>
                    <HistoryTimestamp>
                      {new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {' · '}
                      {entry.updatedBy}
                    </HistoryTimestamp>
                    <HistoryText>
                      Status updated to: <strong>{getStatusLabel(entry.status)}</strong>
                    </HistoryText>
                    {entry.notes && (
                      <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                        {entry.notes}
                      </div>
                    )}
                    {entry.attachments && entry.attachments.length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {entry.attachments.map((attachment, idx) => (
                          <a
                            key={idx}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none', display: 'flex', gap: 4 }}
                          >
                            📎 {attachment.name}
                            <span style={{ color: '#9ca3af' }}>
                              ({attachmentTypeOptions.find(opt => opt.value === attachment.type)?.label || 'Other'})
                            </span>
                          </a>
                        ))}
                      </div>
                    )}
                  </HistoryContent>
                </HistoryItem>
              ))}
            </div>
          </CardContainer>
        </div>
      </div>

      {/* Remove LayoutSide - no sidebar needed */}

      {/* Confirmation Modal for Ready for Dispatch */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{ maxWidth: 500, margin: 16 }}>
            <CardContainer variant="elevated" padding="lg">
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: '0 0 8px 0' }}>
                🔒 Confirm Ready for Dispatch
              </h3>
              <p style={{ fontSize: 14, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
                Marking production as <strong>Ready for Dispatch</strong> will:
              </p>
              <ul style={{ fontSize: 14, color: '#6b7280', margin: '12px 0', paddingLeft: 20, lineHeight: 1.8 }}>
                <li>Notify Ops team (Scheduling unlocked)</li>
                <li>Notify Sales team</li>
                <li><strong>Lock this status</strong> - cannot be changed afterward</li>
                <li>Lock expected completion date</li>
              </ul>
              <p style={{ fontSize: 14, color: '#dc2626', margin: '12px 0 0 0', fontWeight: 500 }}>
                Are you sure production is complete and ready?
              </p>
            </div>
            <FormActions style={{ marginTop: 0 }}>
              <Button onClick={handleSave} disabled={isSaving}>{isSaving ? 'Saving...' : 'Yes, Mark as Ready'}</Button>
              <Button variant="outline" onClick={() => setShowConfirmModal(false)} disabled={isSaving}>Cancel</Button>
            </FormActions>
            </CardContainer>
          </div>
        </div>
      )}
    </Page>
  );
};
